<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
/* global axios */

// 后台顶部服务器状态栏(定制 2026-08-11)。
// 仅管理员可见。每 60s 轮询 GET /server_status,显示磁盘/内存/CPU。
// 颜色阈值: <70 绿 / 70-85 黄 / >85 红。
const props = defineProps({
  accountId: { type: [String, Number], default: '' },
});

const store = useStore();
const status = ref(null);
const error = ref(false);
let timer = null;

const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);

const colorFor = percent => {
  if (percent >= 85) return 'text-red-11';
  if (percent >= 70) return 'text-amber-11';
  return 'text-green-11';
};
const barColorFor = percent => {
  if (percent >= 85) return 'bg-red-500';
  if (percent >= 70) return 'bg-amber-500';
  return 'bg-green-500';
};

const fetchStatus = async () => {
  if (!isAdmin.value || !props.accountId) return;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${props.accountId}/server_status`
    );
    status.value = data;
    error.value = false;
  } catch {
    error.value = true;
  }
};

onMounted(() => {
  fetchStatus();
  timer = setInterval(fetchStatus, 60000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div
    v-if="isAdmin"
    class="flex items-center gap-5 px-4 h-8 text-xs bg-n-solid-2 border-b border-n-slate-4 text-n-slate-11 flex-shrink-0"
  >
    <span class="font-medium text-n-slate-10">服务器状态</span>

    <template v-if="error">
      <span class="text-red-11">状态获取失败</span>
    </template>
    <template v-else-if="status">
      <!-- 磁盘 -->
      <div class="flex items-center gap-1.5">
        <span>磁盘</span>
        <div class="w-16 h-1.5 rounded-full bg-n-slate-5 overflow-hidden">
          <div
            class="h-full rounded-full transition-all"
            :class="barColorFor(status.disk.percent)"
            :style="{ width: `${status.disk.percent}%` }"
          />
        </div>
        <span :class="colorFor(status.disk.percent)" class="tabular-nums font-medium">
          {{ status.disk.percent }}%
        </span>
        <span class="text-n-slate-9 tabular-nums">
          ({{ status.disk.used_gb }}/{{ status.disk.total_gb }}G)
        </span>
      </div>

      <!-- 内存 -->
      <div class="flex items-center gap-1.5">
        <span>内存</span>
        <div class="w-16 h-1.5 rounded-full bg-n-slate-5 overflow-hidden">
          <div
            class="h-full rounded-full transition-all"
            :class="barColorFor(status.memory.percent)"
            :style="{ width: `${status.memory.percent}%` }"
          />
        </div>
        <span :class="colorFor(status.memory.percent)" class="tabular-nums font-medium">
          {{ status.memory.percent }}%
        </span>
        <span class="text-n-slate-9 tabular-nums">
          ({{ status.memory.used_gb }}/{{ status.memory.total_gb }}G)
        </span>
      </div>

      <!-- CPU -->
      <div class="flex items-center gap-1.5">
        <span>CPU</span>
        <span :class="colorFor(status.cpu.percent)" class="tabular-nums font-medium">
          {{ status.cpu.percent }}%
        </span>
        <span class="text-n-slate-9 tabular-nums">
          ({{ status.cpu.load_1 }} / {{ status.cpu.cores }}核)
        </span>
      </div>

      <!-- 未处理拦截提醒(定制 2026-08-25): 有未处理的攻击拦截时红色显示, 点击跳风暴报表处理 -->
      <router-link
        v-if="status.interception_unhandled > 0"
        :to="`/app/${props.accountId}/storm_reports`"
        class="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/40 hover:bg-red-500/25 transition-colors"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        <span class="text-red-11 font-medium">
          未处理拦截 {{ status.interception_unhandled }}
        </span>
      </router-link>
    </template>
    <template v-else>
      <span class="text-n-slate-9">加载中…</span>
    </template>
  </div>
</template>
